import { Plaque } from "./Plaque";
import type { KothRoundState } from "./useKothRound";

/**
 * Bulletin board on a wall showing the game rules.
 *
 * Source: distilled from docs/koth-pumpfun-design.md §§ 2 (Economic loop)
 * and 3 (Game mechanic). If the whitepaper drifts, update buildRulesLines
 * below — this function is the single source of truth for what players read
 * on the wall.
 */

interface RulesBoardProps {
  position: [number, number, number];
  rotation?: [number, number, number];
  round?: KothRoundState | null;
}

function buildRulesLines(round: KothRoundState | null | undefined): string[] {
  const snap = round?.paramsSnapshot;
  const vigPctText =
    typeof snap?.vigBps === "number" ? `${snap.vigBps / 100}%` : "5%";
  const skimPctText =
    typeof snap?.protocolSkimBps === "number"
      ? `${snap.protocolSkimBps / 100}%`
      : "5%";
  const dynPctText =
    typeof snap?.dynamicFeeBps === "number" && snap.dynamicFeeBps > 0
      ? `${snap.dynamicFeeBps / 100}%`
      : null;
  return [
    "⚡ ENABLE RAPID-FIRE (top-left button) to skip",
    "  the wallet confirmation popup on every touch.",
    "  It mints a 15-min throwaway key that signs",
    "  touches for you. Revoke any time.",
    "",
    "HOW TO WIN",
    "• Connect a wallet, then START a round (10 min).",
    "• TOUCH the throne to become king. Each touch",
    "  costs SOL — fee rises 0.0001 SOL per touch" +
      (dynPctText ? ` or ${dynPctText} of pot, whichever is larger.` : "."),
    "• Every touch extends the timer. When it hits",
    "  zero, the last toucher is king and takes 50%.",
    "• Runners-up in the recent-touch list get",
    "  20% / 10% / 2.5%. Protocol skim: 2%.",
    "",
    `House fee: ${vigPctText} of each touch + ${skimPctText} of round pot.`,
    "  Unclaimed winnings expire 30 days after season close.",
    "",
    "RULES",
    "• You can't touch your own throne — wait for",
    "  a challenger to dethrone you first.",
    "• 2-second cooldown between your own touches.",
    "• Mid-round touches still pay — don't snipe.",
    "• When the timer expires, anyone can END the",
    "  round and collect a 0.0005 SOL keeper bounty.",
    "",
    "No RNG. Skill, timing, and awareness.",
  ];
}

export function RulesBoard({ position, rotation, round }: RulesBoardProps): JSX.Element {
  return (
    <Plaque
      position={position}
      rotation={rotation}
      width={4.4}
      height={5.0}
      backerColor="#2a1f10"
      title="HOW TO PLAY"
      titleColor="#f4d36a"
      titleSize={0.28}
      lines={buildRulesLines(round ?? null)}
      bodyColor="#e8e8ef"
      bodySize={0.16}
      lineSpacing={1.18}
      align="left"
    />
  );
}
